/**
 * The Machine Board (#492): what one computer holds, read-only.
 *
 * Every figure comes from `buildMachineBoard`; this file only draws it. The
 * default arrangement is a structured board - summary first, then Workers,
 * terminals, runtimes, local models, projects, capacity and remote AI - and the
 * other views re-arrange the same model rather than reading anything new.
 *
 * Phase 1 is read-only. The only controls here navigate: open a Worker, its
 * terminal, its task, a runtime's usage detail, or the machine's existing
 * capacity controls. Nothing assigns, pins, opens, closes or moves anything,
 * and there is no drag and drop.
 */
import { useMemo, useState, type ReactElement } from 'react';
import {
  DEFAULT_BOARD_FILTERS, MACHINE_BOARD_VIEWS, activeFilterCount, filterBoardWorkers,
  type BoardLocalRuntime, type BoardRuntime, type BoardTerminal, type BoardWorker,
  type MachineBoardFilters, type MachineBoardModel, type MachineBoardView,
} from '../lib/machine-board';
import { MachineMeterBar } from './LiveDock';
import { WorkerStatusIndicator } from './WorkerStatusIndicator';
import '../engine-room.css';
import '../machine-board.css';

export interface MachineBoardMachineRef {
  readonly nodeId: string;
  readonly name: string;
  readonly presence: string;
}

export interface MachineBoardProps {
  readonly model: MachineBoardModel;
  /** Every machine, so the owner can switch boards without going back. */
  readonly machines: readonly MachineBoardMachineRef[];
  readonly initialView?: MachineBoardView;
  readonly onClose: () => void;
  readonly onSwitchMachine: (nodeId: string) => void;
  /** Open this Worker's own surface, which holds its one terminal. */
  readonly onOpenWorker: (workerId: string) => void;
  /** Open the existing Work Board, where tasks live. */
  readonly onOpenTask: (taskId: string) => void;
  /** Open this runtime's lane in the existing AI Usage detail. */
  readonly onOpenUsage: (meterKey: string) => void;
  /** Open this machine's existing capacity and limit controls. */
  readonly onOpenCapacity: (nodeId: string) => void;
}

function clock(value: string | null): string {
  if (value === null) return 'Unknown';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return 'Unknown';
  return at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function presenceTone(presence: string): 'ok' | 'attention' | 'danger' | 'neutral' {
  if (presence === 'connected') return 'ok';
  if (presence === 'stale') return 'attention';
  if (presence === 'disconnected') return 'danger';
  return 'neutral';
}

function stanceTone(stance: string): 'ok' | 'attention' | 'neutral' {
  if (stance === 'accepting') return 'ok';
  if (stance === 'unknown') return 'neutral';
  return 'attention';
}

const INTELLIGENCE_LABELS = { local: 'Local model', cloud: 'Cloud model', unknown: 'Model location unknown' } as const;

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Summary({ model, machines, onSwitchMachine, onClose, onOpenCapacity }: {
  readonly model: MachineBoardModel;
  readonly machines: readonly MachineBoardMachineRef[];
  readonly onSwitchMachine: (nodeId: string) => void;
  readonly onClose: () => void;
  readonly onOpenCapacity: (nodeId: string) => void;
}): ReactElement {
  const summary = model.summary;
  return (
    <section className="board-summary" aria-label={`${summary.name} summary`} data-presence={summary.presence}>
      <div className="board-summary-top">
        <button type="button" className="link board-back" onClick={onClose}>← Engine Room</button>
        {machines.length > 1 && (
          <div className="board-switch" role="group" aria-label="Machines">
            {machines.map((machine) => (
              <button
                type="button"
                key={machine.nodeId}
                className={machine.nodeId === summary.nodeId ? 'chip on' : 'chip'}
                aria-pressed={machine.nodeId === summary.nodeId}
                data-presence={machine.presence}
                onClick={() => onSwitchMachine(machine.nodeId)}
              >
                {machine.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <header className="board-summary-head">
        <h1>{summary.name}</h1>
        <span className="engine-chip" data-tone={presenceTone(summary.presence)}>{summary.presenceLabel}</span>
        <span className="engine-chip" data-tone={stanceTone(summary.stance)}>Taking new work: {summary.stanceLabel}</span>
        <span className="engine-chip engine-chip-quiet">Profile: {summary.profileLabel}</span>
      </header>
      {summary.truthNote !== null && <p className="board-truth-note" role="note">{summary.truthNote}</p>}
      <dl className="board-counts">
        <div><dt>Workers busy</dt><dd>{summary.workersActive} of {summary.workersHere} here</dd></div>
        <div><dt>Worker seats</dt><dd>{summary.seatsText}</dd></div>
        <div><dt>Active runs</dt><dd>{summary.activeRuns ?? 'Unknown'}</dd></div>
        <div><dt>Live terminals</dt><dd>{model.activeTerminalCount}{model.terminalsComplete ? '' : '+'}</dd></div>
        <div><dt>Last heartbeat</dt><dd>{clock(summary.lastSeenAt)}</dd></div>
        <div data-tone={summary.stale ? 'attention' : undefined}>
          <dt>Last reading</dt><dd>{clock(summary.observedAt)}{summary.readingAgeLabel ? ` · ${summary.readingAgeLabel}` : ''}</dd>
        </div>
      </dl>
      <div className="board-summary-meters machine-meters wide">
        {summary.meters.map((meter) => <MachineMeterBar key={meter.key} meter={meter} />)}
      </div>
      <p className="board-dispatch-line">
        {summary.dispatchLine}
        {' '}
        <button type="button" className="link" onClick={() => onOpenCapacity(summary.nodeId)}>Machine limits</button>
      </p>
      <small className="muted">
        {summary.versionLine}
        {summary.cpuCount === null ? '' : ` · ${summary.cpuCount} processors`}
      </small>
    </section>
  );
}

/** Worker → Terminal → Runtime/Model → Task, one link each, absent links said plainly. */
function Chain({ worker }: { readonly worker: BoardWorker }): ReactElement {
  return (
    <ol className="board-chain" aria-label={`${worker.label} relationships`}>
      <li data-link="worker"><span className="board-chain-kind">Worker</span><strong>{worker.label}</strong></li>
      <li data-link="terminal" data-empty={worker.terminal === null ? 'true' : undefined}>
        <span className="board-chain-kind">Terminal</span>
        {worker.terminal === null ? <span className="muted">None</span> : (
          <span><code>{worker.terminal.shortId}</code> · {worker.terminal.phaseLabel}</span>
        )}
      </li>
      <li data-link="runtime" data-empty={worker.runtime.runtimeLabel === null ? 'true' : undefined}>
        <span className="board-chain-kind">Runtime / model</span>
        {worker.runtime.runtimeLabel === null ? <span className="muted">None recorded</span> : (
          <span>
            {worker.runtime.runtimeLabel}
            {worker.runtime.modelKey !== null && <> · {worker.runtime.modelKey}</>}
          </span>
        )}
      </li>
      <li data-link="task" data-empty={worker.taskTitle === null ? 'true' : undefined}>
        <span className="board-chain-kind">Task</span>
        {worker.taskTitle === null ? <span className="muted">No task</span> : <span>{worker.taskTitle}</span>}
      </li>
    </ol>
  );
}

function WorkerCard({ worker, onOpenWorker, onOpenTask }: {
  readonly worker: BoardWorker;
  readonly onOpenWorker: (workerId: string) => void;
  readonly onOpenTask: (taskId: string) => void;
}): ReactElement {
  return (
    <article className="board-worker" data-worker={worker.workerId} data-active={worker.active ? 'true' : 'false'}
      data-intelligence={worker.runtime.intelligence}>
      <header className="board-worker-head">
        <WorkerStatusIndicator status={worker.status} />
        <button type="button" className="board-worker-name" onClick={() => onOpenWorker(worker.workerId)}
          title={`Open ${worker.label}`}>
          {worker.label}
        </button>
        <span className="engine-chip" data-intelligence={worker.runtime.intelligence}>
          {INTELLIGENCE_LABELS[worker.runtime.intelligence]}
        </span>
        {worker.runtime.pinned && <span className="engine-chip engine-chip-quiet">Pinned</span>}
        {worker.elapsed !== null && <span className="muted board-elapsed">{worker.elapsed}</span>}
      </header>
      <Chain worker={worker} />
      <dl className="board-facts">
        <div><dt>Provider</dt><dd>{worker.runtime.providerLabel ?? 'Unknown'}</dd></div>
        <div><dt>Project</dt><dd>{worker.projectName ?? (worker.projectId === null ? 'None' : 'Name not visible')}</dd></div>
        <div><dt>Worktree</dt><dd>{worker.worktree === null ? 'Not reported' : <code>{worker.worktree}</code>}</dd></div>
        <div><dt>Session</dt><dd>{worker.sessionLifecycle ?? 'None'}</dd></div>
      </dl>
      <small className="muted board-intelligence-line">{worker.runtime.intelligenceLine}</small>
      {worker.blocker !== null && <p className="board-warning" data-kind="blocker">{worker.blocker}</p>}
      {worker.capacityWarning !== null && <p className="board-warning" data-kind="capacity">{worker.capacityWarning}</p>}
      <div className="row board-actions">
        <button type="button" className="link" onClick={() => onOpenWorker(worker.workerId)}>
          {worker.terminal === null ? 'Open Worker' : 'Open Worker & terminal'}
        </button>
        {worker.taskId !== null && (
          <button type="button" className="link" onClick={() => onOpenTask(worker.taskId!)}>Open task</button>
        )}
      </div>
    </article>
  );
}

function TerminalTable({ terminals, complete, onOpenWorker }: {
  readonly terminals: readonly BoardTerminal[];
  readonly complete: boolean;
  readonly onOpenWorker: (workerId: string) => void;
}): ReactElement {
  return (
    <section className="board-section" aria-label="Terminals and sessions" data-board-section="terminals">
      <header className="engine-section-head">
        <h2>Terminals &amp; sessions</h2>
        <small className="muted">
          Canonical persistent terminals on this machine{complete ? '' : ' · owner terminals not loaded yet'}
        </small>
      </header>
      {terminals.length === 0 ? <p className="muted">No terminal is recorded on this machine.</p> : (
        <div className="board-table-wrap">
          <table className="board-table">
            <thead>
              <tr><th>Session</th><th>Owner</th><th>Process</th><th>State</th><th>Directory</th><th>Keyboard</th><th>Last activity</th></tr>
            </thead>
            <tbody>
              {terminals.map((terminal) => (
                <tr key={terminal.sessionId} data-phase={terminal.phase} data-session={terminal.sessionId}>
                  <td><code title={terminal.sessionId}>{terminal.shortId}</code></td>
                  <td>
                    {terminal.workerId === null ? <span className="muted">No Worker</span> : (
                      <button type="button" className="link" onClick={() => onOpenWorker(terminal.workerId!)}>
                        {terminal.workerLabel}
                      </button>
                    )}
                  </td>
                  <td>{[terminal.runtimeLabel, terminal.process].filter(Boolean).join(' · ') || 'Unknown'}</td>
                  <td><span className="board-phase" data-phase={terminal.phase}>{terminal.phaseLabel}</span></td>
                  <td>{terminal.cwd === null ? <span className="muted">Not reported</span> : <code>{terminal.cwd}</code>}</td>
                  <td>{terminal.inputHolder ?? <span className="muted">Free</span>}</td>
                  <td>{clock(terminal.lastActivityAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RuntimeCard({ runtime, onOpenUsage }: {
  readonly runtime: BoardRuntime;
  readonly onOpenUsage: (meterKey: string) => void;
}): ReactElement {
  return (
    <article className="board-runtime" data-runtime={runtime.runtimeKey} data-availability={runtime.availability}
      data-idle={runtime.idle ? 'true' : 'false'}>
      <header className="board-runtime-head">
        <strong>{runtime.label}</strong>
        <span className="engine-chip" data-availability={runtime.availability}>
          {runtime.availability === 'available' ? 'Available' : runtime.availability === 'unavailable' ? 'Unavailable' : 'Unknown'}
        </span>
        {runtime.version !== null && <span className="muted">v{runtime.version}</span>}
      </header>
      <small className="muted">{runtime.availabilityLine}</small>
      <dl className="board-facts">
        <div>
          <dt>Installed</dt>
          <dd>
            {runtime.connections.some((row) => row.installed === true) || runtime.models.length > 0
              ? 'Yes · 1 capability'
              : runtime.connections.length > 0 && runtime.connections.every((row) => row.installed === false) ? 'No' : 'Unknown'}
          </dd>
        </div>
        <div><dt>Active sessions</dt><dd>{runtime.activeSessions}</dd></div>
        <div><dt>Workers using</dt><dd>{runtime.workersUsing.length === 0 ? 'None' : runtime.workersUsing.join(', ')}</dd></div>
        <div>
          <dt>Usage</dt>
          <dd>
            {runtime.usage ?? 'No reading'}
            {runtime.usageMeterKey !== null && (
              <>{' '}<button type="button" className="link" onClick={() => onOpenUsage(runtime.usageMeterKey!)}>detail</button></>
            )}
          </dd>
        </div>
      </dl>
      {runtime.connections.length > 0 && (
        <ul className="board-list">
          {runtime.connections.map((connection) => (
            <li key={connection.connectionKey}>
              <span>{connection.label}</span>
              <span className="muted">
                {connection.installed === null ? 'install Unknown' : connection.installed ? 'installed' : 'not installed'}
                {' · '}
                {connection.authState === 'no_auth' ? 'no sign-in needed'
                  : connection.authState === 'signed_in' ? 'signed in'
                    : connection.authState === 'signed_out' ? 'signed out' : 'sign-in Unknown'}
                {connection.note !== null && ` · ${connection.note}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {runtime.models.length > 0 && (
        <ul className="board-list" aria-label={`${runtime.label} models`}>
          {runtime.models.map((model) => (
            <li key={model.offeringKey} data-intelligence={model.intelligence}>
              <code>{model.modelKey}</code>
              <span className="muted">
                {model.intelligence === 'local' ? 'local' : `cloud · ${model.providerLabel}`}
                {' · '}
                {model.workersUsing.length === 0 ? 'idle' : model.workersUsing.join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      {runtime.servesRoles.length > 0 && <small className="muted">Also serving: {runtime.servesRoles.join(', ')}</small>}
    </article>
  );
}

function LocalModels({ runtime }: { readonly runtime: BoardLocalRuntime }): ReactElement {
  return (
    <article className="board-runtime board-local" data-connection={runtime.connectionKey} data-reachable={runtime.reachable}>
      <header className="board-runtime-head">
        <strong>{runtime.label}</strong>
        <span className="engine-chip"
          data-availability={runtime.reachable === 'reachable' ? 'available' : runtime.reachable === 'unreachable' ? 'unavailable' : 'unknown'}>
          {runtime.reachable === 'reachable' ? 'Reachable' : runtime.reachable === 'unreachable' ? 'Not reachable' : 'Unknown'}
        </span>
        {runtime.version !== null && <span className="muted">v{runtime.version}</span>}
      </header>
      <small className="muted">{runtime.quotaLine}</small>
      {!runtime.installedKnown && <small className="muted">Installed models not reported.</small>}
      {!runtime.loadedKnown && <small className="muted">Loaded models not reported.</small>}
      {runtime.models.length === 0 ? <p className="muted">No local model reported.</p> : (
        <ul className="board-list">
          {runtime.models.map((model) => (
            <li key={model.name} data-model-state={model.state}>
              <code>{model.name}</code>
              <span className="muted">
                {model.stateLabel}
                {model.size !== null && ` · ${model.size}`}
                {model.processor !== null && ` · ${model.processor}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <small className="muted">Reported {clock(runtime.observedAt)}</small>
    </article>
  );
}

function CapacitySection({ model, onOpenCapacity }: {
  readonly model: MachineBoardModel;
  readonly onOpenCapacity: (nodeId: string) => void;
}): ReactElement {
  const capacity = model.capacity;
  return (
    <section className="board-section" aria-label="Capacity" data-board-section="capacity">
      <header className="engine-section-head">
        <h2>Capacity</h2>
        <button type="button" className="link" onClick={() => onOpenCapacity(model.summary.nodeId)}>Open machine limits</button>
      </header>
      <dl className="board-facts board-facts-wide">
        <div><dt>Profile</dt><dd>{capacity.profileLabel}</dd></div>
        <div><dt>Seats total</dt><dd>{capacity.seatsConfigured ?? 'Unknown'}</dd></div>
        <div><dt>Seats used</dt><dd>{capacity.seatsActive ?? 'Unknown'}</dd></div>
        <div><dt>Seats free</dt><dd>{capacity.seatsFree ?? 'Unknown'}</dd></div>
        <div><dt>Memory reserve</dt><dd>{capacity.reserveMemoryPercent}%</dd></div>
        <div><dt>Video memory reserve</dt><dd>{capacity.reserveGraphicsPercent}%</dd></div>
        <div><dt>CPU ceiling</dt><dd>{capacity.cpuCeilingPercent === null ? 'Off' : `${capacity.cpuCeilingPercent}% sustained`}</dd></div>
        <div><dt>GPU ceiling</dt><dd>{capacity.graphicsCeilingPercent === null ? 'Off' : `${capacity.graphicsCeilingPercent}% sustained`}</dd></div>
        <div data-tone={capacity.stance === 'accepting' ? undefined : 'attention'}><dt>Taking new work</dt><dd>{capacity.stanceLabel}</dd></div>
      </dl>
      <div className="machine-meters wide">
        {capacity.meters.map((meter) => <MachineMeterBar key={meter.key} meter={meter} />)}
      </div>
      <p className="board-dispatch-line">{capacity.dispatchLine}</p>
      {capacity.holdBackReason !== null && <p className="board-warning" data-kind="capacity">Held back: {capacity.holdBackReason}</p>}
      {capacity.guards.map((guard) => <small key={guard.dimension} className="muted">{guard.line}</small>)}
      <p className="board-safe-line"><strong>Can safely run next:</strong> {capacity.safeAdditionalLine}</p>
    </section>
  );
}

function Topology({ model, workers }: {
  readonly model: MachineBoardModel;
  readonly workers: readonly BoardWorker[];
}): ReactElement {
  return (
    <section className="board-section" aria-label="Topology" data-board-section="topology">
      <header className="engine-section-head">
        <h2>Topology</h2>
        <small className="muted">The same relationships as a tree. Read-only.</small>
      </header>
      <ul className="board-tree" role="tree" aria-label={`${model.summary.name} topology`}>
        <li role="treeitem" aria-expanded="true">
          <strong>{model.summary.name}</strong>
          <ul role="group">
            {workers.map((worker) => (
              <li role="treeitem" key={worker.workerId} aria-expanded="true">
                <strong>{worker.label}</strong>
                <ul role="group">
                  <li role="treeitem">Terminal: {worker.terminal === null ? 'none' : `${worker.terminal.shortId} (${worker.terminal.phaseLabel})`}</li>
                  <li role="treeitem">Runtime: {worker.runtime.runtimeLabel ?? 'none recorded'}</li>
                  {worker.runtime.modelKey !== null && (
                    <li role="treeitem">Model: {worker.runtime.modelKey} ({INTELLIGENCE_LABELS[worker.runtime.intelligence].toLowerCase()})</li>
                  )}
                  {worker.taskTitle !== null && <li role="treeitem">Task: {worker.taskTitle}</li>}
                </ul>
              </li>
            ))}
            {model.localRuntimes.map((runtime) => (
              <li role="treeitem" key={runtime.connectionKey} aria-expanded="true">
                <strong>{runtime.label}</strong>
                <ul role="group">
                  {runtime.models.map((item) => <li role="treeitem" key={item.name}>{item.name} · {item.stateLabel}</li>)}
                </ul>
              </li>
            ))}
            {model.terminals.filter((terminal) => terminal.workerId === null).map((terminal) => (
              <li role="treeitem" key={terminal.sessionId}>Terminal {terminal.shortId} (no Worker) · {terminal.phaseLabel}</li>
            ))}
          </ul>
        </li>
      </ul>
    </section>
  );
}

function Filters({ model, filters, onChange }: {
  readonly model: MachineBoardModel;
  readonly filters: MachineBoardFilters;
  readonly onChange: (next: MachineBoardFilters) => void;
}): ReactElement {
  const runtimeOptions = model.runtimes.filter((runtime) => runtime.workersUsing.length > 0);
  const modelOptions = [...new Set(model.workers.map((worker) => worker.runtime.modelKey).filter((key): key is string => key !== null))].sort();
  const count = activeFilterCount(filters);
  return (
    <div className="board-filters" role="group" aria-label="Filter Workers">
      <label className="board-toggle">
        <input type="checkbox" checked={filters.activeOnly}
          onChange={(event) => onChange({ ...filters, activeOnly: event.target.checked })} />
        Active only
      </label>
      <select aria-label="Runtime" value={filters.runtime ?? ''}
        onChange={(event) => onChange({ ...filters, runtime: event.target.value || null })}>
        <option value="">All runtimes</option>
        {runtimeOptions.map((runtime) => <option key={runtime.runtimeKey} value={runtime.runtimeKey}>{runtime.label}</option>)}
      </select>
      <select aria-label="Model" value={filters.model ?? ''}
        onChange={(event) => onChange({ ...filters, model: event.target.value || null })}>
        <option value="">All models</option>
        {modelOptions.map((key) => <option key={key} value={key}>{key}</option>)}
      </select>
      <select aria-label="Project" value={filters.project ?? ''}
        onChange={(event) => onChange({ ...filters, project: event.target.value || null })}>
        <option value="">All projects</option>
        {model.projects.map((project) => <option key={project.projectId} value={project.projectId}>{project.name}</option>)}
      </select>
      <select aria-label="Local or cloud" value={filters.intelligence ?? ''}
        onChange={(event) => onChange({ ...filters, intelligence: (event.target.value || null) as MachineBoardFilters['intelligence'] })}>
        <option value="">Local &amp; cloud</option>
        <option value="local">Local models</option>
        <option value="cloud">Cloud models</option>
        <option value="unknown">Location unknown</option>
      </select>
      <select aria-label="Availability" value={filters.availability ?? ''}
        onChange={(event) => onChange({ ...filters, availability: (event.target.value || null) as MachineBoardFilters['availability'] })}>
        <option value="">Any state</option>
        <option value="busy">Busy</option>
        <option value="available">Available</option>
        <option value="blocked">Blocked</option>
      </select>
      {count > 0 && <button type="button" className="link" onClick={() => onChange(DEFAULT_BOARD_FILTERS)}>Clear {count}</button>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

export function MachineBoard({
  model, machines, initialView = 'worker', onClose, onSwitchMachine, onOpenWorker, onOpenTask, onOpenUsage, onOpenCapacity,
}: MachineBoardProps): ReactElement {
  const [view, setView] = useState<MachineBoardView>(initialView);
  const [filters, setFilters] = useState<MachineBoardFilters>(DEFAULT_BOARD_FILTERS);
  const workers = useMemo(() => filterBoardWorkers(model.workers, filters), [model.workers, filters]);
  const shownIds = useMemo(() => new Set(workers.map((worker) => worker.workerId)), [workers]);
  const filtering = activeFilterCount(filters) > 0;
  const workerList = (
    <section className="board-section" aria-label="Workers" data-board-section="workers">
      <header className="engine-section-head">
        <h2>Workers on {model.summary.name}</h2>
        <small className="muted">
          {filtering ? `Showing ${workers.length} of ${model.workers.length}` : `${model.workers.length} Worker${model.workers.length === 1 ? '' : 's'}`}
          {' · '}a Worker is a stable identity, not the machine, terminal, runtime or task
        </small>
      </header>
      {model.workers.length === 0
        ? <p className="muted">No Worker is placed on this machine.</p>
        : workers.length === 0
          ? <p className="muted">No Worker matches these filters.</p>
          : (
            <div className="board-workers">
              {workers.map((worker) => (
                <WorkerCard key={worker.workerId} worker={worker} onOpenWorker={onOpenWorker} onOpenTask={onOpenTask} />
              ))}
            </div>
          )}
    </section>
  );

  return (
    <main className="work machine-board" aria-label={`Machine Board: ${model.summary.name}`}
      data-node={model.summary.nodeId} data-view={view}>
      <Summary model={model} machines={machines} onSwitchMachine={onSwitchMachine} onClose={onClose}
        onOpenCapacity={onOpenCapacity} />

      <nav className="board-views" aria-label="Board view">
        {MACHINE_BOARD_VIEWS.map((item) => (
          <button type="button" key={item.id} className={item.id === view ? 'chip on' : 'chip'}
            aria-pressed={item.id === view} onClick={() => setView(item.id)}>
            {item.label}
          </button>
        ))}
      </nav>

      {view !== 'capacity' && <Filters model={model} filters={filters} onChange={setFilters} />}

      {model.facts.length > 0 && (
        <ul className="board-facts-line" aria-label="Machinist reading">
          {model.facts.map((fact) => <li key={fact}>{fact}</li>)}
        </ul>
      )}

      {view === 'worker' && (
        <div className="board-grid">
          {workerList}
          <TerminalTable terminals={model.terminals.filter((terminal) => terminal.workerId === null || shownIds.has(terminal.workerId))}
            complete={model.terminalsComplete} onOpenWorker={onOpenWorker} />
        </div>
      )}

      {view === 'runtime' && (
        <div className="board-grid">
          <section className="board-section" aria-label="Runtimes and CLIs" data-board-section="runtimes">
            <header className="engine-section-head">
              <h2>Runtimes &amp; CLIs on {model.summary.name}</h2>
              <small className="muted">Installed is a capability; sessions are counted from real terminals</small>
            </header>
            {model.runtimes.length === 0 ? <p className="muted">No runtime is offered or probed on this machine.</p> : (
              <div className="board-runtimes">
                {model.runtimes
                  .filter((runtime) => !filtering || runtime.workersUsing.some((label) => workers.some((worker) => worker.label === label)))
                  .map((runtime) => <RuntimeCard key={runtime.runtimeKey} runtime={runtime} onOpenUsage={onOpenUsage} />)}
              </div>
            )}
          </section>
          <section className="board-section" aria-label="Local models" data-board-section="local-models">
            <header className="engine-section-head">
              <h2>Ollama / local models</h2>
              <small className="muted">Runs on this machine · no quota</small>
            </header>
            {model.localRuntimes.length === 0 ? <p className="muted">No local runtime reports on this machine.</p> : (
              <div className="board-runtimes">
                {model.localRuntimes.map((runtime) => <LocalModels key={runtime.connectionKey} runtime={runtime} />)}
              </div>
            )}
          </section>
        </div>
      )}

      {view === 'capacity' && (
        <div className="board-grid">
          <CapacitySection model={model} onOpenCapacity={onOpenCapacity} />
          <section className="board-section" aria-label="Seats" data-board-section="seats">
            <header className="engine-section-head"><h2>Who holds a seat</h2></header>
            {model.workers.length === 0 ? <p className="muted">No Worker is placed here.</p> : (
              <ul className="board-list">
                {model.workers.map((worker) => (
                  <li key={worker.workerId} data-active={worker.active ? 'true' : 'false'}>
                    <span>{worker.label}</span>
                    <span className="muted">
                      {worker.active ? (worker.taskTitle ?? 'working') : 'idle'}
                      {worker.runtime.runtimeLabel !== null && ` · ${worker.runtime.runtimeLabel}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {view === 'project' && (
        <div className="board-grid">
          <section className="board-section" aria-label="Projects and worktrees" data-board-section="projects">
            <header className="engine-section-head"><h2>Projects &amp; worktrees</h2></header>
            {model.projects.length === 0 ? <p className="muted">No project is bound on this machine.</p> : (
              <div className="board-runtimes">
                {model.projects
                  .filter((project) => !filtering || workers.some((worker) => worker.projectId === project.projectId))
                  .map((project) => (
                    <article key={project.projectId} className="board-runtime" data-project={project.projectId}>
                      <header className="board-runtime-head"><strong>{project.name}</strong></header>
                      <dl className="board-facts">
                        <div><dt>Workers</dt><dd>{project.workers.join(', ')}</dd></div>
                        <div><dt>Tasks</dt><dd>{project.tasks.length === 0 ? 'None running' : project.tasks.join('; ')}</dd></div>
                      </dl>
                      {project.worktrees.length === 0
                        ? <small className="muted">No worktree path reported.</small>
                        : <ul className="board-list">{project.worktrees.map((tree) => <li key={tree}><code>{tree}</code></li>)}</ul>}
                      {project.routing !== null && <small className="muted">{project.routing}</small>}
                      {project.warning !== null && <p className="board-warning" data-kind="collision">{project.warning}</p>}
                    </article>
                  ))}
              </div>
            )}
          </section>
          {workerList}
        </div>
      )}

      {view === 'topology' && <Topology model={model} workers={workers} />}

      <small className="muted board-footnote">
        Read-only. Nothing on this board assigns, pins, opens, closes or moves anything.
      </small>
    </main>
  );
}
